// The forecast for the screens: shared/forecast.ts over the synced tables (the nightly forecast_base plus
// overrides, scoped adjustments and "forecast like"), worked out again only when one of them changes (or
// the day does) and shared by every component that asks, the purchasing plan included. An override or an
// item's settings changing only works out the items whose rows changed. Inventory health and the planned
// purchases calendar are derived the same way, only when a page asks for them; the calendar waits for an
// idle moment so the page never blocks on it.
import { useEffect, useState } from "react";
import { buildForecasts, type Forecasts, inventoryHealth, stockAtHhl, vendorCosts } from "../../../shared/forecast.ts";
import { type Plan, type PlannedOrder, plannedPurchases, planToday } from "../../../shared/planning.ts";
import { heldAtHhl } from "../../../shared/presales.ts";
import { useReservations } from "./presales.ts";
import { store, useRows } from "./sync.ts";

/** Adds the key of every row that's new, changed or gone between two versions of a table (rows keep their identity until they change). */
export function changedKeys<T>(before: readonly T[], after: readonly T[], key: (row: T) => string, into: Set<string>) {
  const was = new Set(before);
  const is = new Set(after);
  for (const r of after) if (!was.has(r)) into.add(key(r));
  for (const r of before) if (!is.has(r)) into.add(key(r));
  return into;
}

let last: { inputs: readonly unknown[]; forecasts: Forecasts } | null = null;
const OVERRIDES = 3;
const PLANS = 5;

const FORECAST_TABLES = [
  "item",
  "forecast_base",
  "sales_summary",
  "forecast_override",
  "forecast_adjustment",
  "item_plan",
  "shopify_product",
  "vendor_item",
  "vendor",
  "project",
  "forecast_run",
] as const;
type ForecastInputs = ReturnType<typeof useForecastInputs>;
const useForecastInputs = () =>
  [
    useRows("item"),
    useRows("forecast_base"),
    useRows("sales_summary"),
    useRows("forecast_override"),
    useRows("forecast_adjustment"),
    useRows("item_plan"),
    useRows("shopify_product"),
    useRows("vendor_item"),
    useRows("vendor"),
    useRows("project"),
    useRows("forecast_run"),
  ] as const;

export function useForecasts(): Forecasts {
  return forecastsFor(useForecastInputs());
}

/** Check against the plan --- the same outside React (an idle job): the store's rows are useRows()' rows, so the cache is shared. */
export function forecastsNow(): Forecasts {
  return forecastsFor(FORECAST_TABLES.map((t) => store.rows(t)) as unknown as ForecastInputs);
}

function forecastsFor(inputs: ForecastInputs): Forecasts {
  const today = planToday();
  const changed = last ? inputs.map((x, i) => x !== last?.inputs[i]) : null;
  if (!last || last.forecasts.today !== today || changed?.some(Boolean)) {
    const [items, bases, sales, overrides, adjustments, itemPlans, products, vendorItems, vendors, projects, runs] = inputs;
    const data = { today, items, bases, sales, overrides, adjustments, itemPlans, products, vendorItems, vendors, projects, run: runs[0] ?? null };
    // Only overrides or item settings changed: work out just those items.
    const prev = last?.forecasts;
    const only =
      prev && prev.today === today && changed?.every((c, i) => !c || i === OVERRIDES || i === PLANS)
        ? changedKeys(
            last?.inputs[PLANS] as typeof itemPlans,
            itemPlans,
            (p) => p.id,
            changedKeys(last?.inputs[OVERRIDES] as typeof overrides, overrides, (o) => o.item_id, new Set()),
          )
        : null;
    last = { inputs, forecasts: buildForecasts(data, prev && only ? { prev, only } : undefined) };
  }
  return last.forecasts;
}

let lastHealth: { inputs: unknown[]; health: ReturnType<typeof inventoryHealth> } | null = null;

export function useHealth() {
  const forecasts = useForecasts();
  const inputs = [
    forecasts,
    useRows("item"),
    useRows("stock_level"),
    useRows("location"),
    useRows("vendor_item"),
    useRows("sales_summary"),
    useRows("planning_settings"),
    useReservations(), // Pre-sales --- held stock isn't for sale
  ] as const;
  if (!lastHealth || lastHealth.inputs.some((x, i) => x !== inputs[i])) {
    const [, items, stock, locations, links, sales, settings, reservations] = inputs;
    lastHealth = {
      inputs: [...inputs],
      health: inventoryHealth({
        today: forecasts.today,
        items: items.filter((i) => i.kind === "sell" && !i.archived),
        forecasts,
        available: stockAtHhl(stock, locations),
        vendorCost: vendorCosts(links),
        sales,
        overstockMonths: Number(settings[0]?.overstock_months ?? 12),
        held: heldAtHhl(reservations, forecasts.today),
      }),
    };
  }
  return lastHealth.health;
}

let lastOrders: { plan: Plan; orders: PlannedOrder[] } | null = null;
const idle = (fn: () => void) => {
  const w = window as Window & { requestIdleCallback?: (fn: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(fn, { timeout: 1000 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = setTimeout(fn, 50);
  return () => clearTimeout(id);
};

/**
 * The plan's orders over the next 12 months, worked out once per plan in an idle moment after the page has
 * drawn. Until then (and while a newer plan is being worked out) it gives the last result and `stale`.
 */
export function usePlannedPurchases(plan: Plan): { orders: PlannedOrder[] | null; stale: boolean } {
  const [, setDone] = useState(0);
  const fresh = lastOrders?.plan === plan;
  useEffect(() => {
    if (lastOrders?.plan === plan) return;
    return idle(() => {
      lastOrders = { plan, orders: plannedPurchases(plan) };
      setDone((n) => n + 1);
    });
  }, [plan]);
  return { orders: lastOrders?.orders ?? null, stale: !fresh };
}
